<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { PortableTextSpan } from '@portabletext/types';
  import type { MarkComponentProps } from '@portabletext/svelte';
  import type { Modal as ModalSchemaType } from '$lib/sanity/schemas/components/Modal';
  import { modals } from '$lib/stores/modals';
  import { anchorNavigationStore, type AnchorNavigationStore } from '$lib/stores/anchorNavigation';
  import { linkPagePath } from '$lib/utils/routes';
  import type { LinkPage } from '$lib/sanity/schemas';
  import { LinkDecorator } from '../Common';

  interface Props {
    // Property custom marks receive from @portabletext/svelte when redered
    portableText: MarkComponentProps<{
      _key: string;
      href?: string;
      url?: string;
      page?: LinkPage;
      type?: string;
      anchorKey?: string;
      modal?: ModalSchemaType;
    }>;
    children?: Snippet;
  }

  let { portableText, children }: Props = $props();

  let modalBtn: HTMLElement | undefined = $state();
  let linkEl: HTMLAnchorElement | undefined = $state();

  /**
   * Returns true if child portable text span is wrapped with targetMark mark.
   */
  function childHasMark(targetMark: string): boolean {
    const linkWrappedSpan = portableText.parentBlock?.children
      .filter((block) => block._type === 'span')
      .find((block): block is PortableTextSpan => {
        return block.marks?.includes(portableText.value._key) ?? false;
      });

    return linkWrappedSpan?.marks?.includes(targetMark) ?? false;
  }

  function useAnchorId(anchorNavigationStore: AnchorNavigationStore, anchorKey: string) {
    const anchor = anchorNavigationStore.anchors.find(
      (anchor) => anchor.markKey === anchorKey && !!anchor.el.offsetParent
    );

    return anchor?.el.getAttribute('id');
  }

  let pageUrl = $derived(portableText.value.page && linkPagePath(portableText.value.page));
  // the property representing regular links was formerly named url but has been changed to href
  // sanity uses href and represents pasted links with the href field, so that will be the field from here
  // when text with links are pasted into sanity, it will not have a a type field, so by default this will be interpreted as a link
  let href = $derived(
    portableText.value.type === 'url' || !portableText.value.type
      ? portableText.value.href || portableText.value.url
      : pageUrl
  );
  let isRelativeLink = $derived(href?.startsWith('/'));

  let anchorId = $derived(
    portableText.value.anchorKey &&
      useAnchorId($anchorNavigationStore, portableText.value.anchorKey)
  );
  let isAnchorLink = $derived(portableText.value.type === 'anchor' && !!anchorId);

  let hasSubscript = $derived(childHasMark('subScript'));
  let hasSuperscript = $derived(childHasMark('superScript'));

  function handleClick($event: Event) {
    $event.stopPropagation();
    if (!isAnchorLink || !anchorId) {
      return;
    }

    const possibleTargets = document.querySelectorAll<HTMLAnchorElement>(`#${anchorId}`);
    const target = Array.from(possibleTargets).find((element) => !!element.offsetParent);

    if (target) {
      const { top } = target.getBoundingClientRect();

      window.gtag &&
        window.gtag('event', 'anchor_click', {
          event_category: 'Anchor Links',
          event_label: anchorId
        });

      window.scrollTo({
        top: top + window.scrollY - $anchorNavigationStore.offset,
        behavior: 'smooth'
      });
    }

    linkEl?.blur();
  }

  function handleOpenModal() {
    if (portableText.value.modal) {
      modals.open(portableText.value.modal._id, modalBtn);
    }
  }

  function handleOpenModalKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleOpenModal();
    }
  }
</script>

{#if href || isAnchorLink}
  <LinkDecorator>
    <a
      bind:this={linkEl}
      class:sub={hasSubscript}
      class:sup={hasSuperscript}
      href={isAnchorLink ? `#${anchorId}` : href}
      target={isAnchorLink || isRelativeLink ? '' : '_blank'}
      onclick={handleClick}
    >
      {@render children?.()}
    </a>
  </LinkDecorator>
{:else if portableText.value.type === 'modal'}
  <LinkDecorator>
    <span
      role="button"
      tabindex="0"
      bind:this={modalBtn}
      onclick={handleOpenModal}
      onkeydown={handleOpenModalKeydown}
    >
      {@render children?.()}
    </span>
  </LinkDecorator>
{:else}
  {@render children?.()}
{/if}

<style lang="scss">
  // Special handling for subscript/superscript in links
  :global(.link-styled a.has-subscript),
  :global(.link-styled a.has-superscript) {
    text-decoration: none;

    :global(sub),
    :global(sup) {
      text-decoration: underline;
    }
  }
</style>
